/**
 * Charts for a v2 model.
 *
 * The charts themselves are v1's, shared as `ChartsGrid`. Only the mapping
 * from statements to series lives here, because that is the one thing that
 * genuinely differs: v2 renamed `fixedAssetsNet` to `netFixedAssets`, and it
 * has no `totalLiabilitiesAndEquity` line at all — its balance sheet presents
 * liabilities and equity separately.
 */

import { ChartsGrid } from '../../ChartsGrid';
import { getForecastContext, type QuarterlyData } from '../../../types/financial-model';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';

/** Add two period series, treating a missing period as zero. */
function sum(a: QuarterlyData, b: QuarterlyData): QuarterlyData {
  const out: QuarterlyData = {};
  for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
    out[key] = (a[key] ?? 0) + (b[key] ?? 0);
  }
  return out;
}

export function ChartsViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);

  if (!model) return null;

  const { pnl, cashflow, balanceSheet, annualSummary } = model.computed;

  // The statements' charts only. The valuation range and driver sensitivity are
  // valuation charts, and valuation is its own discipline with its own Charts tab.
  return (
    <ChartsGrid
      accountingOnly
      data={{
        ctx: getForecastContext(model.settings),
        currency: model.settings.baseCurrency,
        annualSummary,
        totalRevenue: pnl.totalRevenue,
        grossProfit: pnl.grossProfit,
        ebitda: pnl.ebitda,
        profitAfterTax: pnl.profitAfterTax,
        closingCash: cashflow.closingCash,
        netOperatingCashflow: cashflow.netOperatingCashflow,
        netInvestingCashflow: cashflow.netInvestingCashflow,
        netFinancingCashflow: cashflow.netFinancingCashflow,
        fixedAssetsNet: balanceSheet.netFixedAssets,
        totalCurrentAssets: balanceSheet.totalCurrentAssets,
        // v2 carries no combined line, so it is summed here. Against total
        // assets the two coincide exactly when the sheet balances, which is
        // what makes the comparison worth plotting.
        totalLiabilitiesAndEquity: sum(
          balanceSheet.totalLiabilities,
          balanceSheet.totalEquity,
        ),
        tradeDebtors: balanceSheet.tradeDebtors,
        tradeCreditors: balanceSheet.tradeCreditors,
        stocks: balanceSheet.stocks,
        totalAssets: balanceSheet.totalAssets,
        totalLiabilities: balanceSheet.totalLiabilities,
        netAssets: balanceSheet.totalAssetsLessLiabilities,
      }}
    />
  );
}
